import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { SyutGrid, SyutSelect, type SyutGridColumn } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { usePropertyScopeItems } from 'hooks/api/usePropertyScope';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import notificationService from 'services/notificationService';
import { propertyService } from 'api/services/propertyService';
import { nightAuditService } from 'api/services/nightAuditService';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import { formatCurrency } from 'utils/currencyFormat';
import {
  NIGHT_AUDIT_ACTIONS,
  NIGHT_AUDIT_EXCEPTION_LABELS,
  type NightAuditException,
  type NightAuditPosting,
  type NightAuditPreview,
  type NightAuditRun,
  type NightAuditStatus,
  type NightRevision,
} from 'api/types/nightAudit.types';

/**
 * ============================================================================
 * NIGHT AUDIT — close the business date (PMS Phase 2 slice 6a, decision P2-18)
 * ============================================================================
 * One date drives the screen — the date being closed ("Closing"): the first run
 * closes the property's night audit start date, each later run the next date, up
 * to the business date. The body lists that date's problems, exceptions and nights
 * to post as collapsible sections (one open at a time, so the open grid keeps its
 * height), and History. Run closes the date: nothing posts into it afterwards,
 * desk charges fall on the next open date. A failed run leaves the date open and
 * can simply be run again.
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

/** Body sections — one open at a time. */
type Section = 'blockers' | 'exceptions' | 'corrections' | 'postings' | 'history';
type RevisionRow = NightRevision & { id: string };
type ExceptionRow = NightAuditException & { id: string };
type PostingRow = NightAuditPosting & { id: string };
type RunRow = NightAuditRun;

const day = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const stamp = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const isoDay = (v: string) => v.slice(0, 10);

const statusBadge = (code?: string | null, name?: string | null) => {
  const cls = code === 'AUDIT_COMPLETED' ? 'badge-success' : code === 'AUDIT_FAILED' ? 'badge-danger'
    : code === 'AUDIT_IN_PROGRESS' ? 'badge-warning' : 'badge-secondary';
  return <span className={`badge ${cls}`} style={{ fontSize: '0.65rem' }}>{name || code || '—'}</span>;
};

const NightAuditScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const canRun = allowedActions.includes(NIGHT_AUDIT_ACTIONS.submit);

  const { defaultPropertyId } = useDefaultProperty();
  const [propertyId, setPropertyId] = useState('');
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const propertyLabel = usePropertyLabel(propertyId);

  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const [status, setStatus] = useState<NightAuditStatus | null>(null);
  const [preview, setPreview] = useState<NightAuditPreview | null>(null);
  const [history, setHistory] = useState<RunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Section | null>('exceptions');
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [failures, setFailures] = useState<NightAuditException[]>([]);
  const [startInput, setStartInput] = useState('');
  const [savingStart, setSavingStart] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) { setStatus(null); setPreview(null); setHistory([]); return; }
    setLoading(true);
    try {
      const [s, h] = await Promise.all([nightAuditService.status(propertyId), nightAuditService.history(propertyId)]);
      setStatus(s);
      setHistory(h);
      setStartInput(isoDay(s.startDate || s.businessDate));
      if (!s.startDate && !s.closedThrough) setFilterOpen(true);
      const p = s.canRun ? await nightAuditService.preview(propertyId) : null;
      setPreview(p);
      setAcknowledged(false);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load night audit.');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { setFailures([]); void load(); }, [load]);

  const run = useCallback(async () => {
    if (!preview) return;
    setRunning(true);
    try {
      const result = await nightAuditService.run({
        propertyId,
        auditDate: isoDay(preview.auditDate),
        acknowledgeExceptions: acknowledged,
      });
      setFailures(result.failures);
      if (result.closed) {
        notificationService.toast.success(`${day(result.run.auditDate)} closed — ${result.run.linesPosted} line(s) posted.`);
      } else {
        notificationService.toast.error(`${result.failures.length} posting(s) failed — ${day(result.run.auditDate)} stays open. Fix and run again.`);
      }
      setConfirming(false);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The night audit could not run.');
      setConfirming(false);
    } finally {
      setRunning(false);
    }
  }, [preview, propertyId, acknowledged, load]);

  const saveStart = useCallback(async () => {
    if (!startInput) return;
    setSavingStart(true);
    try {
      await nightAuditService.setStartDate(propertyId, startInput);
      notificationService.toast.success(`Night audit starts on ${day(startInput)}.`);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The start date was not saved.');
    } finally {
      setSavingStart(false);
    }
  }, [propertyId, startInput, load]);

  // ---- grids ---------------------------------------------------------------------------------
  const exceptionRows = useMemo<ExceptionRow[]>(
    () => [...failures, ...(preview?.exceptions ?? [])].map((e, i) => ({ ...e, id: `${e.kind}-${i}` })),
    [failures, preview]);
  const revisionRows = useMemo<RevisionRow[]>(
    () => (preview?.revisions ?? []).map((r) => ({ ...r, id: r.chargeId })),
    [preview]);
  const revisionColumns = useMemo<SyutGridColumn<RevisionRow>[]>(() => [
    { id: 'roomNumber', header: 'Room', accessorKey: 'roomNumber', size: 70, cell: ({ row }) => <b>{row.original.roomNumber || '—'}</b> },
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 130 },
    { id: 'chargeType', header: 'Type', accessorKey: 'chargeType', size: 80 },
    { id: 'serviceDate', header: 'Night of', accessorKey: 'serviceDate', size: 110, cell: ({ row }) => day(row.original.serviceDate) },
    { id: 'postedAmount', header: 'Posted', accessorKey: 'postedAmount', size: 110, cell: ({ row }) => formatCurrency(row.original.postedAmount) },
    {
      id: 'newAmount', header: 'Now', accessorKey: 'newAmount', size: 120,
      cell: ({ row }) => (row.original.newAmount > 0
        ? formatCurrency(row.original.newAmount)
        : <span className="badge badge-warning" style={{ fontSize: '0.62rem' }}>Not stayed</span>),
    },
    { id: 'why', header: 'Why', accessorKey: 'why', size: 380, cell: ({ row }) => <span style={{ whiteSpace: 'normal', fontSize: '0.8rem' }}>{row.original.why}</span> },
  ], []);

  const postingRows = useMemo<PostingRow[]>(
    () => (preview?.postings ?? []).map((p, i) => ({ ...p, id: `${p.folioId}-${p.chargeType}-${p.serviceDate}-${i}` })),
    [preview]);

  const exceptionColumns = useMemo<SyutGridColumn<ExceptionRow>[]>(() => [
    {
      id: 'kind', header: 'Exception', accessorKey: 'kind', size: 190,
      cell: ({ row }) => (
        <span className={`badge ${row.original.kind === 'POSTING_FAILED' ? 'badge-danger' : 'badge-warning'}`} style={{ fontSize: '0.65rem' }}>
          {NIGHT_AUDIT_EXCEPTION_LABELS[row.original.kind] ?? row.original.kind}
        </span>
      ),
    },
    { id: 'roomNumber', header: 'Room', accessorKey: 'roomNumber', size: 70, cell: ({ row }) => row.original.roomNumber || '—' },
    { id: 'guestName', header: 'Guest', accessorKey: 'guestName', size: 180, cell: ({ row }) => row.original.guestName || '—' },
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 140, cell: ({ row }) => row.original.bookingNumber || '—' },
    {
      id: 'message', header: 'What to do', accessorKey: 'message', size: 520,
      cell: ({ row }) => <span style={{ whiteSpace: 'normal', fontSize: '0.8rem' }}>{row.original.message}</span>,
    },
  ], []);

  const postingColumns = useMemo<SyutGridColumn<PostingRow>[]>(() => [
    { id: 'roomNumber', header: 'Room', accessorKey: 'roomNumber', size: 70, cell: ({ row }) => <b>{row.original.roomNumber || '—'}</b> },
    { id: 'guestName', header: 'Guest', accessorKey: 'guestName', size: 170, cell: ({ row }) => row.original.guestName || '—' },
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 130 },
    { id: 'chargeType', header: 'Type', accessorKey: 'chargeType', size: 80 },
    {
      id: 'serviceDate', header: 'For', accessorKey: 'serviceDate', size: 110,
      cell: ({ row }) => (
        <span>
          {day(row.original.serviceDate)}
          {row.original.isBackPosted && <span className="badge badge-info" style={{ fontSize: '0.6rem', marginLeft: '0.3rem' }}
            title={row.original.chargeDate === row.original.serviceDate ? 'Back-posted on its own date' : `Posts on ${day(row.original.chargeDate)}`}>earlier</span>}
        </span>
      ),
    },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 300 },
    { id: 'folioNumber', header: 'Folio', accessorKey: 'folioNumber', size: 130, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.folioNumber || '—'}</span> },
    {
      id: 'amount', header: 'Amount', accessorKey: 'amount', size: 120,
      cell: ({ row }) => (
        <span title={row.original.isTaxInclusive ? 'Rate includes tax' : 'Before tax'}>
          {formatCurrency(row.original.amount)}{row.original.isTaxInclusive ? ' incl.' : ''}
        </span>
      ),
    },
  ], []);

  const historyColumns = useMemo<SyutGridColumn<RunRow>[]>(() => [
    { id: 'auditDate', header: 'Date', accessorKey: 'auditDate', size: 110, cell: ({ row }) => <b>{day(row.original.auditDate)}</b> },
    { id: 'auditStatusCode', header: 'Status', accessorKey: 'auditStatusCode', size: 110, cell: ({ row }) => statusBadge(row.original.auditStatusCode, row.original.auditStatusName) },
    {
      id: 'occupiedRooms', header: 'Occupied', accessorKey: 'occupiedRooms', size: 110,
      cell: ({ row }) => `${row.original.occupiedRooms} / ${row.original.totalRooms} (${row.original.occupancyPercent}%)`,
    },
    { id: 'roomRevenue', header: 'Room', accessorKey: 'roomRevenue', size: 110, cell: ({ row }) => formatCurrency(row.original.roomRevenue) },
    { id: 'mealRevenue', header: 'Meal plan', accessorKey: 'mealRevenue', size: 100, cell: ({ row }) => formatCurrency(row.original.mealRevenue) },
    { id: 'addonRevenue', header: 'Add-ons', accessorKey: 'addonRevenue', size: 100, cell: ({ row }) => formatCurrency(row.original.addonRevenue) },
    { id: 'otherRevenue', header: 'Desk', accessorKey: 'otherRevenue', size: 100, cell: ({ row }) => formatCurrency(row.original.otherRevenue) },
    { id: 'adr', header: 'ADR', accessorKey: 'adr', size: 100, cell: ({ row }) => formatCurrency(row.original.adr) },
    { id: 'revpar', header: 'RevPAR', accessorKey: 'revpar', size: 100, cell: ({ row }) => formatCurrency(row.original.revpar) },
    {
      id: 'movements', header: 'Arr / Dep / NS', accessorKey: 'arrivalsCount', size: 110,
      cell: ({ row }) => `${row.original.arrivalsCount} / ${row.original.departuresCount} / ${row.original.noShowCount}`,
    },
    {
      id: 'linesPosted', header: 'Lines', accessorKey: 'linesPosted', size: 90,
      cell: ({ row }) => `${row.original.linesPosted}${row.original.backPostedNights ? ` (${row.original.backPostedNights} earlier)` : ''}`,
    },
    { id: 'taxPosted', header: 'Tax', accessorKey: 'taxPosted', size: 100, cell: ({ row }) => formatCurrency(row.original.taxPosted) },
    {
      id: 'completedAt', header: 'Closed by', accessorKey: 'completedAt', size: 190,
      cell: ({ row }) => row.original.failedReason
        ? <span style={{ color: 'var(--danger, #dc2626)', whiteSpace: 'normal', fontSize: '0.78rem' }}>{row.original.failedReason}</span>
        : `${row.original.completedByName || '—'} · ${stamp(row.original.completedAt)}`,
    },
  ], []);

  // ---- header band -----------------------------------------------------------------------------
  // One date drives the screen: the date being closed. The grid lists its nights, Run closes it.
  const closingDate = preview?.auditDate ?? status?.nextAuditDate;
  const scopeItems = usePropertyScopeItems(propertyId, closingDate ?? undefined);
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : ''), primary: true },
    {
      label: 'Closing', value: closingDate ? day(closingDate) : '—', primary: true, icon: 'lock',
      title: 'The business date this run closes — the nights listed below are this date\'s',
    },
    { label: 'Business date', value: day(status?.businessDate), icon: 'calendar', title: 'Property time less the business-day cut-off' },
    ...scopeItems,
    {
      label: 'Audit starts', value: status?.startDate ? day(status.startDate) : 'Not set', icon: 'calendar',
      title: status?.startDateSetByName ? `Set by ${status.startDateSetByName} · ${stamp(status.startDateSetAt)}` : 'The first date night audit closes; no night before it is posted',
    },
    { label: 'Closed through', value: status?.closedThrough ? day(status.closedThrough) : 'Not yet audited', icon: 'checkCircle' },
    { label: 'Charges post on', value: day(status?.openDate), icon: 'clock', title: 'The open business date desk charges and reversals take' },
  ];

  const blockers = preview?.blockers ?? [];
  const hasExceptions = (preview?.exceptions.length ?? 0) > 0;
  const runDisabled = !preview || !preview.canRun || (hasExceptions && !acknowledged) || running;
  const runTooltip = !preview ? (status?.whyNot || 'Nothing to close yet')
    : blockers.length > 0 ? `${blockers.length} problem(s) stop the run — see "Cannot run yet"`
    : hasExceptions && !acknowledged ? 'Tick "I have reviewed the exceptions" in the Exceptions section first'
    : `Post the nights of ${day(preview.auditDate)} and close it`;

  // Open the section that needs attention whenever the data changes.
  useEffect(() => {
    if (!preview) { setOpen('history'); return; }
    setOpen(preview.blockers.length > 0 ? 'blockers' : (preview.exceptions.length > 0 || failures.length > 0) ? 'exceptions' : 'postings');
  }, [preview, failures.length]);

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(() => { setFailures([]); void load(); }),
    {
      id: 'run',
      icon: <IconSystem.lock size={16} />,
      label: running ? 'Running…' : closingDate ? `Close ${day(closingDate)}` : 'Run night audit',
      tooltip: runTooltip,
      onClick: () => setConfirming(true),
      disabled: runDisabled,
      variant: 'primary',
      visible: canRun,
    },
  ];

  // The start date is a property setting, changeable until the first close — set from the panel.
  const startEditable = !!status && canRun && !status.startDateLocked;

  /**
   * One collapsible section of the body. Only one is open at a time and the open one takes the
   * remaining height (its grid scrolls inside it), so a long list — of problems or exceptions —
   * never squeezes the others off the screen: their headers stay one line each.
   */
  const section = (id: Section, title: string, count: number | null, tone: 'danger' | 'warning' | 'default',
    summary: React.ReactNode, body: React.ReactNode, extra?: React.ReactNode) => {
    const isOpen = open === id;
    const color = tone === 'danger' ? 'var(--danger, #dc2626)' : tone === 'warning' ? 'var(--warning-dark, #b45309)' : 'var(--text-primary)';
    return (
      <div key={id} style={{
        display: 'flex', flexDirection: 'column', minHeight: 0,
        flex: isOpen ? '1 1 0' : '0 0 auto', borderBottom: '1px solid var(--border-color)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 0.75rem', background: 'var(--bg-secondary)', flexShrink: 0 }}>
          <button type="button" onClick={() => setOpen(isOpen ? null : id)} aria-expanded={isOpen}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color, fontWeight: 700, fontSize: '0.82rem' }}>
            {isOpen ? <IconSystem.chevronDown size={14} /> : <IconSystem.chevronRight size={14} />}
            {title}
            {count !== null && (
              <span className={`badge ${tone === 'danger' ? 'badge-danger' : tone === 'warning' ? 'badge-warning' : 'badge-secondary'}`} style={{ fontSize: '0.65rem' }}>{count}</span>
            )}
          </button>
          <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
            {summary}
          </span>
          {extra}
        </div>
        {isOpen && <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{body}</div>}
      </div>
    );
  };

  const sections: React.ReactNode[] = [];
  if (preview && (preview.warnings?.length ?? 0) > 0) {
    sections.push(
      <div key="warnings" style={{ flexShrink: 0, fontSize: '0.78rem', color: 'var(--warning-text, #92400e)', background: 'var(--warning-bg, #fef3c7)', padding: '0.4rem 0.75rem', borderRadius: 6 }}>
        {preview.warnings!.map((w) => <div key={w}>{w}</div>)}
      </div>);
  }
  if (preview && blockers.length > 0) {
    sections.push(section('blockers', 'Cannot run yet', blockers.length, 'danger', blockers[0],
      <ul style={{ margin: 0, padding: '0.5rem 0.75rem 0.5rem 2rem', overflowY: 'auto', flex: 1, minHeight: 0, fontSize: '0.8rem', color: 'var(--danger, #dc2626)' }}>
        {blockers.map((b) => <li key={b} style={{ marginBottom: '0.25rem' }}>{b}</li>)}
      </ul>));
  }
  if (preview) {
    sections.push(section('exceptions', 'Exceptions', exceptionRows.length, exceptionRows.length ? 'warning' : 'default',
      failures.length > 0 ? `${failures.length} posting(s) failed on the last run — fix and run again`
        : exceptionRows.length ? 'Reviewed, not blocking — acknowledge them to run' : 'Nothing to review',
      <SyutGrid data={exceptionRows} columns={exceptionColumns} isLoading={loading} emptyMessage="Nothing to review — the night is clean" />,
      canRun && hasExceptions && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
          I have reviewed the exceptions
        </label>
      )));
    if (revisionRows.length > 0) {
      const delta = revisionRows.reduce((s, r) => s + r.newAmount - r.postedAmount, 0);
      sections.push(section('corrections', 'Corrections to posted nights', revisionRows.length, 'warning',
        `Reversed and re-posted on ${day(preview.auditDate)} — net ${formatCurrency(delta)}; closed dates do not change`,
        <SyutGrid data={revisionRows} columns={revisionColumns} isLoading={loading} emptyMessage="No corrections" />));
    }
    sections.push(section('postings', `To post — nights of ${day(preview.auditDate)}`, postingRows.length, 'default',
      <>
        Room nights {preview.roomNights} · Room {formatCurrency(preview.roomAmount)} · Meal plan {formatCurrency(preview.mealAmount)}
        {preview.addonAmount > 0 ? ` · Add-ons ${formatCurrency(preview.addonAmount)}` : ''}
        {preview.backPostedNights ? ` · ${preview.backPostedNights} late night(s)` : ''}
      </>,
      <SyutGrid data={postingRows} columns={postingColumns} isLoading={loading} emptyMessage="Nothing to post for this date" />));
  }
  sections.push(section('history', 'History', history.length, 'default',
    history[0] ? <>Last: {statusBadge(history[0].auditStatusCode, history[0].auditStatusName)} {day(history[0].auditDate)}</> : 'No date closed yet',
    <SyutGrid data={history} columns={historyColumns} isLoading={loading} emptyMessage="No night audit has run for this property yet" />));

  const footer = preview ? (
    <>
      <span><b>{day(preview.auditDate)}</b>{preview.isFirstAudit ? ' · first audit' : ''}</span>
      <span>Occupied {preview.occupiedRooms}{preview.complimentaryRooms ? ` (${preview.complimentaryRooms} complimentary)` : ''}</span>
      <span style={{ color: 'var(--text-muted)' }}>{preview.taxCalculated ? 'Tax per night by the tax engine' : 'Tax calculation is off — lines post untaxed'}</span>
    </>
  ) : status?.whyNot ? (
    <span style={{ color: 'var(--text-muted)' }}>{status.whyNot}</span>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Night Audit"
      description="Close the business date: post each night's room, meal plan and booked add-ons with their tax, then lock the date."
      icon={<IconSystem.lock size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: propertyId ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  isAsync
                  loadOptions={loadPropertyOptions}
                  defaultOptions
                  cacheOptions={false}
                  isClearable={false}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  placeholder="Select property..."
                  size="sm"
                  menuPortalTarget={document.body}
                />
              </div>
              {startEditable && (
                <div className="filter-section">
                  <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                    Night audit start date <span className="text-danger">*</span>
                  </label>
                  <input type="date" className="form-input" value={startInput} onChange={(e) => setStartInput(e.target.value)}
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '0.2rem 0 0.4rem' }}>
                    The first date night audit closes. Nights before it are never posted (stays of the UAT period);
                    later dates close one at a time up to the business date. Fixed once a date is closed.
                  </div>
                  <button type="button" className="btn btn-primary btn-sm"
                    disabled={!startInput || savingStart || isoDay(status?.startDate || '') === startInput}
                    onClick={() => { void saveStart(); }}>
                    {status?.startDate ? 'Change start date' : 'Set start date'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!propertyId ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property in the filters.</div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {sections}
          </div>
        )}
      </ReportBody>

      <ConfirmDialog
        open={confirming}
        title={`Close ${preview ? day(preview.auditDate) : ''}?`}
        message={preview
          ? `${preview.postings.length} line(s) for the nights of ${day(preview.auditDate)} will be posted to guest folios. `
            + `After this nothing can be posted on ${day(preview.auditDate)}; charges move to the next open date.`
          : ''}
        confirmLabel="Run and close"
        onConfirm={() => { void run(); }}
        onCancel={() => setConfirming(false)}
        loading={running}
        loadingMessage="Posting the night…"
      />
    </EnterpriseScreenShell>
  );
};

export default NightAuditScreen;
